// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import Accordion from 'client/components/accordion/accordion';
import Checkmark from 'client/components/checkmark';
import EmailSubscriptionContainer from 'client/components/subscribe/email';
import HeroContainer from 'client/components/heroContainer';
import Panel from 'client/components/accordion/panel';
import CONTENTFUL_ENTRY_ID from 'client/constants/contentfulEntry';
import {
  COVID19_FINANCE_TAB_CLICK,
  COVID19_FINANCE_STATE_SELECT,
  COVID19_FINANCE_QUICK_LINK_CLICK,
  COVID19_FINANCE_EMAIL_SUBSCRIBED,
} from 'client/constants/amplitudeTracker';
import COLORS from 'client/constants/colors';
import { Tracking } from 'web-core/src/tracking';
import IntroSection, { type IntroType } from './shared/introSection';
import ResourcesNav, { type NavType } from './shared/resourcesNav';
import ContentFilter from './shared/contentFilter';
import { trackAccordionClick } from './utils/tracking';

import '../styles/guidePage.scss';

type StateQuickLinksType = {
  [key: string]: string,
};

type FilterContentItemType = {
  state: string,
  content: React$Element<*>,
};

type Props = {
  fetchContentful: Function,
  contentfulData: {
    accordion: Array<{
      title: string,
      content: string,
    }>,
    accordionHeader: string,
    contentFilter: {
      header: string,
      description: string,
    },
    emailSubscription: {
      header: string,
      description: string,
    },
    hero: Object,
    intro: IntroType,
    nav: NavType,
    stateQuickLinks: StateQuickLinksType,
  },
};

const QUICK_LINKS_KEYS = {
  COVID_UPDATES: 'covidUpdates',
  FILE_BY_PHONE: 'fileByPhone',
  FILE_ONLINE: 'fileOnline',
  FOOD_ASSISTANCE: 'foodAssistance',
  FOOD_BANK: 'foodBank',
  GENERAL_INFO: 'generalInformation',
  MEDICAL_ASSISTANCE: 'medicalAssistance',
  RENT_ASSISTANCE: 'rentAssistance',
  UTILITIES_ASSISTANCE: 'utilitiesAssistance',
};

const quickLinksNameMap = {
  [QUICK_LINKS_KEYS.COVID_UPDATES]: 'State COVID-19 updates',
  [QUICK_LINKS_KEYS.FILE_BY_PHONE]: 'File by phone for unemployment',
  [QUICK_LINKS_KEYS.FILE_ONLINE]: 'File online for unemployment',
  [QUICK_LINKS_KEYS.FOOD_ASSISTANCE]: 'Food assistance',
  [QUICK_LINKS_KEYS.FOOD_BANK]: 'Food bank',
  [QUICK_LINKS_KEYS.GENERAL_INFO]: 'General unemployment information',
  [QUICK_LINKS_KEYS.MEDICAL_ASSISTANCE]: 'Medical assistance',
  [QUICK_LINKS_KEYS.RENT_ASSISTANCE]: 'Rent assistance',
  [QUICK_LINKS_KEYS.UTILITIES_ASSISTANCE]: 'Utilities assistance',
};

class FinanceGuide extends Component<Props> {
  componentDidMount() {
    this.props.fetchContentful(CONTENTFUL_ENTRY_ID.COVID_19_FINANCE_GUIDE);
  }

  trackStateSelect = (state: string) => {
    Tracking.track(COVID19_FINANCE_STATE_SELECT, { state });
  };

  renderQuickLinks = (stateLinks: Object, key: string) => {
    const stateTranslations = _.get(this.props, 'translations.common.states', {});
    const linksDisplayOrder = [
      QUICK_LINKS_KEYS.GENERAL_INFO,
      QUICK_LINKS_KEYS.FILE_ONLINE,
      QUICK_LINKS_KEYS.FILE_BY_PHONE,
      QUICK_LINKS_KEYS.COVID_UPDATES,
      QUICK_LINKS_KEYS.FOOD_BANK,
      QUICK_LINKS_KEYS.FOOD_ASSISTANCE,
      QUICK_LINKS_KEYS.RENT_ASSISTANCE,
      QUICK_LINKS_KEYS.UTILITIES_ASSISTANCE,
      QUICK_LINKS_KEYS.MEDICAL_ASSISTANCE,
    ];
    return (
      <div className="state-quick-links pad-top">
        <div className="header bold">{stateTranslations[key]} quick links:</div>
        {stateLinks.description && <p className="pad-top">{stateLinks.description}</p>}
        <ul>
          {_.map(
            linksDisplayOrder,
            name =>
              stateLinks.links[name] && (
                <li key={quickLinksNameMap[name]}>
                  <a
                    href={stateLinks.links[name]}
                    rel="noopener noreferrer"
                    target="_blank"
                    onClick={() =>
                      Tracking.track(COVID19_FINANCE_QUICK_LINK_CLICK, {
                        link: quickLinksNameMap[name],
                      })
                    }>
                    {quickLinksNameMap[name]}
                  </a>
                </li>
              ),
          )}
        </ul>
      </div>
    );
  };

  renderQuickLinkItems = (stateQuickLinks: StateQuickLinksType): Array<FilterContentItemType> =>
    _.map(stateQuickLinks, (stateLinks: Object, stateKey: string) => ({
      key: `${stateKey}-QuickLinkItems`,
      state: stateKey,
      content: this.renderQuickLinks(stateLinks, stateKey),
    }));

  renderStateUnemploymentContent = (
    stateUnemploymentContent: Object,
  ): Array<FilterContentItemType> =>
    _.map(stateUnemploymentContent, (state: string, stateKey: string) => ({
      key: `${stateKey}-UnemploymentContent`,
      state: stateKey,
      content: (
        <div
          dangerouslySetInnerHTML={{
            __html: documentToHtmlString(stateUnemploymentContent[stateKey]),
          }} />
      ),
    }));

  renderEmailSubscription = (stateQuickLinks: StateQuickLinksType, emailSubscription) =>
    _.map(stateQuickLinks, (stateLinks: Object, stateKey: string) => ({
      key: `${stateKey}-EmailSubscription`,
      state: stateKey,
      content: (
        <div className="email-subscription pad-top">
          <h4 className="header bold pad-y">{emailSubscription.header}</h4>
          <div className="pad-bottom">{emailSubscription.description}</div>
          <EmailSubscriptionContainer
            lists={[`covidFinanceEmailSubscription${stateKey}`]}
            className="focus-white-color email-subscription-form"
            id={`covidFinanceEmailSubscription${stateKey}`}
            submitHandler={() =>
              Tracking.track(COVID19_FINANCE_EMAIL_SUBSCRIBED, { state: stateKey })
            }
            subscribeSuccessIcon={<Checkmark checked />}
            subscribeSuccessStyle={{
              display: 'flex',
              alignItems: 'center',
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '2rem',
              color: COLORS.NAVY_2,
            }}
            buttonClassName="primary oep-hover"
            buttonLabel={{ path: 'common.cta.sendMeUpdates' }}
            subscribedLabel={{ path: 'common.label.emailSubscribedShort' }} />
        </div>
      ),
    }));

  render() {
    const {
      contentfulData: {
        accordion,
        accordionHeader,
        contentFilter,
        emailSubscription,
        hero,
        intro,
        nav,
        stateQuickLinks,
        ...stateUnemploymentContent
      },
    } = this.props;

    const contentFilterProps = {
      changeHandler: this.trackStateSelect,
      defaultValue: '',
      description: _.get(contentFilter, 'description', ''),
      filterByKey: 'state',
      header: _.get(contentFilter, 'header', ''),
      input: {
        type: 'SelectState',
        inputGroupProps: {
          label: null,
          commonTranslations: _.get(this.props, 'translations.common', {}),
          stateTranslations: _.get(this.props, 'translations.common.states', {}),
        },
      },
      items: [
        ...this.renderStateUnemploymentContent(stateUnemploymentContent),
        ...this.renderQuickLinkItems(stateQuickLinks),
        ...this.renderEmailSubscription(stateQuickLinks, emailSubscription),
      ],
    };

    return (
      <div className="finance-guide-page guide-page-container">
        <HeroContainer base="finance-guide" {...hero} />
        <div className="content-container">
          {intro && <IntroSection intro={intro} />}
          <ContentFilter {...contentFilterProps} />
          <h3 className="pad-y-2">{accordionHeader}</h3>
          <Accordion onChange={state => trackAccordionClick(COVID19_FINANCE_TAB_CLICK, state)}>
            {_.map(accordion, item => (
              <Panel key={item.title} title={<span>{item.title}</span>}>
                <div dangerouslySetInnerHTML={{ __html: `${item.content}` }} />
              </Panel>
            ))}
          </Accordion>
          {nav && <ResourcesNav nav={nav} />}
        </div>
      </div>
    );
  }
}

export default FinanceGuide;
